/**
 * Fetch wrapper with full non-2xx diagnostics.
 * Always reads response text (even on non-2xx) and surfaces status, headers, body.
 * Use for Edge Function calls so failures show actionable error details.
 */
import { supabase } from "@/integrations/supabase/client";
export type FetchJsonResult<T = unknown> = {
  ok: boolean;
  status: number;
  headers: Headers;
  text: string;
  json: T | null;
};

export async function fetchJson<T = unknown>(
  url: string,
  options?: RequestInit
): Promise<FetchJsonResult<T>> {
  const response = await fetch(url, options);
  const text = await response.text();
  let json: T | null = null;
  try {
    json = text ? (JSON.parse(text) as T) : null;
  } catch {
    // leave json null
  }
  const result: FetchJsonResult<T> = {
    ok: response.ok,
    status: response.status,
    headers: response.headers,
    text,
    json,
  };
  if (!response.ok) {
    const requestId =
      response.headers.get("x-request-id") ??
      response.headers.get("cf-ray") ??
      response.headers.get("supabase-request-id") ??
      (json && typeof (json as { requestId?: string }).requestId === "string"
        ? (json as { requestId: string }).requestId
        : null);
    const snippet = text.slice(0, 2000);
    const reqIdStr = requestId ? ` requestId=${requestId}` : "";
    const errFromBody =
      json && typeof (json as { error?: string }).error === "string"
        ? (json as { error: string }).error
        : snippet;
    const msg = [
      `Edge Function HTTP ${response.status}`,
      `url=${url}`,
      requestId ? `requestId=${requestId}` : null,
      errFromBody || "(no body)",
    ]
      .filter(Boolean)
      .join(" | ");
    throw new Error(msg, {
      cause: { url, status: response.status, text: snippet, requestId },
    });
  }
  return result;
}

/** Callback for intermediate SSE events (file_complete, progress with phase). */
export type StreamEventHandler = (eventType: string, data: unknown) => void;

/**
 * Read an SSE (text/event-stream) response and return the data from the "complete" event.
 * Throws on "error" events or if the stream ends without a "complete" event.
 * Optionally forwards intermediate events (file_complete) to onStreamEvent callback.
 */
async function readSSEStream<T>(response: Response, url: string, onStreamEvent?: StreamEventHandler): Promise<T> {
  if (!response.body) throw new Error("SSE response has no body");
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let result: T | null = null;
  let lastError: string | null = null;

  const SSE_INACTIVITY_TIMEOUT_MS = 120_000; // 120s — match server-side wall clock deadline (builds can take 2+ minutes)

  while (true) {
    // Race reader.read() against an inactivity timeout
    const timeoutPromise = new Promise<{ done: true; value: undefined }>((_, reject) =>
      setTimeout(() => reject(new Error("SSE inactivity timeout")), SSE_INACTIVITY_TIMEOUT_MS)
    );
    let readResult: ReadableStreamReadResult<Uint8Array>;
    try {
      readResult = await Promise.race([reader.read(), timeoutPromise]);
    } catch (e) {
      // Inactivity timeout — treat as stream end
      console.error("[SSE_TIMEOUT]", { url, message: String(e) });
      reader.cancel().catch(() => {});
      lastError = "Build timed out — no response for 2 minutes. The build may have been too complex. Try again or simplify your prompt.";
      break;
    }
    const { done, value } = readResult;
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // Parse complete SSE event blocks (delimited by double newlines)
    let idx: number;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const block = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);

      let eventType = "";
      let eventData = "";
      for (const line of block.split("\n")) {
        if (line.startsWith("event: ")) eventType = line.slice(7);
        else if (line.startsWith("data: ")) eventData += (eventData ? "\n" : "") + line.slice(6);
      }

      if (eventType === "complete" && eventData) {
        try {
          result = JSON.parse(eventData) as T;
        } catch (parseErr) {
          console.error("[SSE_COMPLETE_PARSE_FAIL]", {
            parseError: String(parseErr),
            dataLength: eventData.length,
            first300: eventData.slice(0, 300),
            last200: eventData.slice(-200),
          });
          lastError = "Failed to parse SSE complete event";
        }
      } else if (eventType === "error" && eventData) {
        console.error("[SSE_ERROR_EVENT]", { url, eventData: eventData.slice(0, 500) });
        try {
          const errObj = JSON.parse(eventData);
          lastError = typeof errObj.error === "string" ? errObj.error : JSON.stringify(errObj);
        } catch { lastError = eventData; }
      } else if (onStreamEvent && eventType === "file_complete" && eventData) {
        // Forward streaming file progress to caller
        try { onStreamEvent(eventType, JSON.parse(eventData)); } catch { /* skip malformed */ }
      } else if (onStreamEvent && eventType === "progress" && eventData) {
        // Forward progress events (file_count, build phases) to caller
        try { onStreamEvent(eventType, JSON.parse(eventData)); } catch { /* skip malformed */ }
      } else if (onStreamEvent && eventType === "plan" && eventData) {
        // Forward plan preview events to caller
        try { onStreamEvent(eventType, JSON.parse(eventData)); } catch { /* skip malformed */ }
      } else if (onStreamEvent && eventType === "clear_files" && eventData) {
        // Forward clear_files event to caller — signals start of new build
        try { onStreamEvent(eventType, JSON.parse(eventData)); } catch { /* skip malformed */ }
      }
    }
  }

  // SSE error events — pass through original error message for friendlyErrorMessage matching.
  // Status/URL preserved in cause for debugging.
  if (lastError) {
    console.error("[SSE_STREAM_FAIL]", { url, error: lastError.slice(0, 500) });
    throw new Error(lastError || "Request failed", { cause: { url, status: 502, text: lastError } });
  }
  if (!result) {
    console.error("[SSE_STREAM_FAIL]", { url, error: "Stream ended without complete event" });
    throw new Error("Connection was interrupted before the response completed", { cause: { url, status: 502 } });
  }

  // Check for error field in the complete event data (same as JSON path)
  const data = result as Record<string, unknown>;
  if (data?.error && typeof data.error === "string") {
    console.error("[SSE_COMPLETE_HAS_ERROR]", { url, error: data.error, debug: (data as any).debug, rawSnippet: ((data as any).rawSnippet ?? "").slice(0, 300) });
    throw new Error(data.error, { cause: { url, status: 502, text: JSON.stringify(data) } });
  }

  return result;
}

/**
 * Invoke a Supabase Edge Function with full non-2xx diagnostics.
 * Handles both JSON and SSE (text/event-stream) responses transparently.
 */
export async function invokeEdgeFunction<T = unknown>(
  name: string,
  body: Record<string, unknown>,
  options?: { signal?: AbortSignal; onStreamEvent?: StreamEventHandler }
): Promise<T> {
  const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL || "").replace(/\/$/, "");
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!supabaseUrl || !anonKey) throw new Error("Missing Supabase config");
  const url = `${supabaseUrl}/functions/v1/${name}`;
  const { data: { session } } = await supabase.auth.getSession();
  let token = session?.access_token || anonKey;

  const doInvoke = async (authToken: string): Promise<T> => {
    let response: Response;
    try {
      response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Connection": "keep-alive",
          apikey: anonKey,
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(body),
        signal: options?.signal,
        // @ts-expect-error - keepalive is valid but not in all TS definitions
        keepalive: false, // Don't use keepalive mode for SSE - it limits body size
      });
    } catch (fetchErr) {
      // Network-level failure (connection refused, DNS failure, network timeout)
      console.error("[EDGE_FN_FETCH_FAIL]", { url, error: fetchErr instanceof Error ? fetchErr.message : String(fetchErr) });
      throw fetchErr;
    }

    // SSE streaming response — parse event stream and return "complete" event data
    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("text/event-stream")) {
      return readSSEStream<T>(response, url, options?.onStreamEvent);
    }

    // Regular JSON response — use existing fetchJson-style logic
    const text = await response.text();
    let json: Record<string, unknown> | null = null;
    try { json = text ? JSON.parse(text) : null; } catch { /* leave null */ }

    if (!response.ok) {
      const requestId =
        response.headers.get("x-request-id") ??
        response.headers.get("cf-ray") ??
        response.headers.get("supabase-request-id") ??
        (json && typeof json.requestId === "string" ? json.requestId as string : null);
      const snippet = text.slice(0, 2000);
      const errFromBody = json && typeof json.error === "string" ? json.error as string : snippet;
      console.error("[EDGE_FN_ERROR]", { url, status: response.status, body: snippet.slice(0, 500), requestId });
      const msg = [
        `Edge Function HTTP ${response.status}`,
        `url=${url}`,
        requestId ? `requestId=${requestId}` : null,
        errFromBody || "(no body)",
      ].filter(Boolean).join(" | ");
      throw new Error(msg, { cause: { url, status: response.status, text: snippet, requestId } });
    }

    if (json?.error && typeof json.error === "string")
      throw new Error(json.error as string, { cause: { url, status: response.status, text } });

    return json as T;
  };

  const isRetryableStatus = (err: unknown): boolean => {
    const cause = err instanceof Error ? (err as Error & { cause?: { status?: number } })?.cause : null;
    if (!cause || typeof cause !== "object") return false;
    const status = (cause as { status?: number }).status;
    return status === 500 || status === 502 || status === 503 || status === 504;
  };

  const MAX_RETRIES = 2;
  let lastErr: unknown;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      return await doInvoke(token);
    } catch (err) {
      lastErr = err;
      const cause = err instanceof Error ? (err as Error & { cause?: { status?: number } })?.cause : null;

      // 401: refresh auth and retry once
      if (attempt === 0 && cause && typeof cause === "object" && (cause as { status?: number }).status === 401) {
        await supabase.auth.refreshSession();
        const { data: { session: newSession } } = await supabase.auth.getSession();
        token = newSession?.access_token || anonKey;
        continue;
      }

      // 500/502/503/504: retry with exponential backoff
      if (attempt < MAX_RETRIES && isRetryableStatus(err)) {
        const delay = (attempt + 1) * 2000; // 2s, 4s
        console.warn(`[invokeEdgeFunction] HTTP 5xx — retry ${attempt + 1}/${MAX_RETRIES} after ${delay}ms`, {
          url, status: cause && typeof cause === "object" ? (cause as { status?: number }).status : undefined,
        });
        await new Promise((r) => setTimeout(r, delay));
        continue;
      }

      throw err;
    }
  }
  throw lastErr;
}
